import React, { useEffect, useRef, useState } from "react";
import CaretIcon from "../components/icons/CaretIcon";
import "./css/FilterPanel.css";

/**
 * Map filter panel
 * Props:
 *  - baseUrl: string (api base url)
 *  - query / setQuery
 *  - selectedCategories / setSelectedCategories
 *  - visiblePoints / onSelectPoint
 */
export default function FilterPanel({
    baseUrl,
    query,
    setQuery,
    selectedCategories,
    setSelectedCategories,
    visiblePoints = [],
    onSelectPoint
}) {
    const [categoryOptions, setCategoryOptions] = useState([{ value: "", label: "" }]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        let cancelled = false;
        async function loadCategoryEnum() {
            try {
                const res = await fetch(`${baseUrl}/api/enums/CategoryTypeEnum`);
                if (!res.ok) return;
                const data = await res.json().catch(() => []);
                if (cancelled) return;
                const opts = [{ value: "", label: "" }, ...data.map(i => ({ value: i.value, label: i.display, color: i.color }))];
                setCategoryOptions(opts);
            } catch (e) {
                console.warn("Failed to load CategoryTypeEnum:", e);
            }
        }
        loadCategoryEnum();
        return () => { cancelled = true; };
    }, [baseUrl]);

    // default to all categories selected once categoryOptions are loaded
    useEffect(() => {
        const values = categoryOptions.filter(o => o.value).map(o => o.value);
        if (values.length > 0 && (!Array.isArray(selectedCategories) || selectedCategories.length === 0)) {
            setSelectedCategories(values);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryOptions]);

    // click-outside close for the dropdown
    useEffect(() => {
        function onDocClick(e) {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) setOpen(false);
        }
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const visibleCategoryValues = categoryOptions.filter(o => o.value).map(o => o.value);
    const allSelected = visibleCategoryValues.length > 0 && selectedCategories.length === visibleCategoryValues.length;

    function toggleCategory(value) {
        setSelectedCategories(prev => {
            const s = new Set(prev || []);
            if (s.has(value)) s.delete(value); else s.add(value);
            return Array.from(s);
        });
    }

    function setAllCategories(selectAll) {
        if (selectAll) {
            setSelectedCategories(visibleCategoryValues);
        } else {
            setSelectedCategories([]);
        }
    }

    const [pointsOpen, setPointsOpen] = useState(false);

    // Added explicit event handling + console.log to diagnose click forwarding
    function handlePointClick(p, e) {
        if (e && typeof e.stopPropagation === "function") {
            e.stopPropagation();
            if (typeof e.preventDefault === "function") e.preventDefault();
        }
        if (typeof onSelectPoint === "function") {
            try {
                onSelectPoint(p);
                setPointsOpen(s => !s);
            } catch (err) {
                console.log("onSelectPoint threw:", err);
            }
            return;
        } else {
            console.log("onSelectPoint is not a function, cannot call");
        }
        // fallback: open view page in new tab if id present
        if (p?.id) {
            window.open(`/manage/view/${encodeURIComponent(p.id)}`, "_blank", "noopener,noreferrer");
        }
    }

    return (
        <div ref={ref} className="filter-panel">
            <div className="row controls-row">
                {/* Category dropdown */}
                <div>
                    <div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm text-start filterCollapseButton"
                            onClick={() => setOpen(o => !o)}
                            aria-expanded={open}
                            aria-haspopup="true"
                        >
                            <div>
                                <strong>Categories </strong>
                                <span>
                                    {Array.isArray(selectedCategories) ? selectedCategories.length : 0} selected
                                </span>
                            </div>
                            <CaretIcon direction={open ? "up" : "down"} />
                        </button>

                        {open && (
                            <div className="card">
                                <div className="form-check mb-2">
                                    <input
                                        id="cat-select-all"
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={e => setAllCategories(e.target.checked)}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="cat-select-all">
                                        Select/Deselect all categories
                                    </label>
                                </div>

                                <div>
                                    {categoryOptions.filter(o => o.value).map(opt => {
                                        const checked = Array.isArray(selectedCategories) && selectedCategories.includes(opt.value);
                                        return (
                                            <div key={opt.value} className="form-check">
                                                <input
                                                    id={`cat-${opt.value}`}
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleCategory(opt.value)}
                                                />
                                                <label
                                                    className={`form-check-label`}
                                                    htmlFor={`cat-${opt.value}`}
                                                >
                                                    <span
                                                        aria-hidden="true"
                                                        className="color-swatch"
                                                        style={{ background: opt.color || "#000" }}
                                                    >
                                                        {checked && (
                                                            <svg
                                                                width="10" height="8"
                                                                viewBox="0 0 12 10"
                                                                aria-hidden="true"
                                                                focusable="false">
                                                                <path
                                                                    d="M1 5l3 3 7-7"
                                                                    stroke="#fff"
                                                                    strokeWidth="2"
                                                                    fill="none"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round" />
                                                            </svg>
                                                        )}
                                                    </span>

                                                    <span>
                                                        {opt.label}
                                                    </span>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Name and address filter */}
                <div>
                    <input
                        className="form-control form-control-sm"
                        placeholder="Filter points by name or address..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        aria-label="Search points by name"
                    />
                </div>
            </div>

            <div className="row points-row">
                {/* Points dropdown toggle (full width below controls) */}
                <div>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm w-100 text-start"
                        aria-expanded={pointsOpen}
                        onClick={() => setPointsOpen(s => !s)}
                    >
                        <div>
                            <strong>Visible Points </strong>
                            <span>
                                {Array.isArray(visiblePoints) ? visiblePoints.length : 0} shown
                            </span>
                        </div>
                        <CaretIcon direction={pointsOpen ? "up" : "down"} />
                    </button>

                    {pointsOpen && (
                        <div className="card mt-2">
                            {Array.isArray(visiblePoints) && visiblePoints.length > 0 ? (
                                visiblePoints.map((p, i) => {
                                    const color = p.colour ?? "#000";
                                    return (
                                        <div
                                            key={p.id ?? `${p.lat}-${p.lng}-${i}`}
                                            onClick={(e) => handlePointClick(p, e)}
                                        >
                                            <span
                                                style={{ background: color }}
                                            />
                                            <div>
                                                <div>
                                                    <span>{p.name ?? ""}</span>
                                                    <span>{p.civicAddress ?? ""}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-muted">No points to show.</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}